import { ApiResponse } from '@nohara-dxdev/oidc-core';
import { MutationConfig, useNotifications } from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api-client';
import { ItemEntity } from '@/types/api';
import { Location } from '@/types/location';

import { getItemTemplateSchemasQueryKey } from './get-item-template-schemas';
import { getItemsQueryKey } from './get-items';

export interface CreateItemDto {
  itemTemplateId: string;
  name: string;
  itemNumber?: string;
  attributes: Record<string, any>;
  quotationTypeId?: string;
  location?: Location | null;
  adjacentLocation?: Location | null;
  locationTypeId?: string;
}

export const createItem = async (
  orgId: string,
  projectId: string,
  item: CreateItemDto,
): Promise<ItemEntity> => {
  const { data } = await api.post<unknown, ApiResponse<ItemEntity>>(
    `/orgs/${orgId}/projects/${projectId}/items`,
    item,
  );

  return data;
};

export interface UseCreateItemOptions {
  mutationConfig?: MutationConfig<(item: CreateItemDto) => Promise<ItemEntity>>;
}

export const useCreateItem = ({ mutationConfig }: UseCreateItemOptions = {}) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const { orgId, projectId } = useScopeIds('org', 'project');

  const { onSuccess, onError, ...restConfig } = mutationConfig ?? {};

  return useMutation({
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: getItemsQueryKey(orgId, projectId) });
      void queryClient.invalidateQueries({ queryKey: getItemTemplateSchemasQueryKey(orgId) });
      notifications.success('items.create.success');
      onSuccess?.(...args);
    },
    onError: (...args) => {
      notifications.error('items.create.error');
      onError?.(...args);
    },
    ...restConfig,
    mutationFn: (item: CreateItemDto) => createItem(orgId, projectId, item),
  });
};
